/**
 * [T-067] Approvals — สัญญาของคำขออนุมัติ N-of-M (W7-CONTRACTS §0 · docs/proposals/w7-contracts-v2.md §2.1)
 * self-contained เหมือน workflow.ts (กัน circular import ระหว่าง `export *`)
 * ApprovalRule (config ของ transition) อยู่ที่ ./workflow.ts แล้ว (Lead scaffold) — ไฟล์นี้เป็นฝั่ง runtime/result เท่านั้น
 */
import { z } from "zod";

/** [F5/P6] ชนิดคำขอ — transition = ผ่านแถว transitions จริง · ที่เหลือ = คำขอ "ทั้ง action" ที่ gate ตาม §2.5 */
export const ApprovalRequestKind = z.enum(["transition", "handoff_transfer", "handoff_return", "merge_close", "snooze", "unsnooze"]);
export type ApprovalRequestKind = z.infer<typeof ApprovalRequestKind>;

export const ApprovalStatus = z.enum(["pending", "approved", "declined", "expired", "cancelled"]);
export type ApprovalStatus = z.infer<typeof ApprovalStatus>;

/** ผู้อนุมัติตาม snapshot ณ ตอนขอ (ชื่อสำหรับแสดงผล — live eligibility ตรวจตอนกด) */
export const PendingApprover = z.object({ id: z.uuid(), display_name: z.string().nullable() });
export type PendingApprover = z.infer<typeof PendingApprover>;

/**
 * ผลลัพธ์ "ต้องอนุมัติก่อน" — body ของ 202 (transition/handoff) · `apply_result` ของ Message (compound) ·
 * `pending_approval` ของ bulk item · body ของ merge/snooze ที่ pend (per-call outcome)
 */
export const TransitionPendingApproval = z.object({
  result: z.literal("pending_approval"),
  approval_id: z.uuid(),
  request_kind: ApprovalRequestKind,
  needed: z.number().int(),
  received: z.number().int(),
  approvers: z.array(PendingApprover),
  expires_at: z.iso.datetime().nullable(),
  ticket_version: z.number().int(),
});
export type TransitionPendingApproval = z.infer<typeof TransitionPendingApproval>;

/** POST /api/v1/approvals/:id/decisions — staff ที่อยู่ในกติกาเท่านั้น */
export const ApprovalDecisionInput = z.object({
  decision: z.enum(["approve", "decline"]),
  note: z.string().max(5000).optional(),
});
export type ApprovalDecisionInput = z.infer<typeof ApprovalDecisionInput>;

export const ApprovalDecisionView = z.object({
  user_id: z.uuid(),
  display_name: z.string().nullable(),
  decision: z.enum(["approve", "decline"]),
  note: z.string().nullable(),
  via: z.enum(["portal", "email", "api"]),
  at: z.iso.datetime(),
});
export type ApprovalDecisionView = z.infer<typeof ApprovalDecisionView>;

/** แถวของ GET /api/v1/tickets/:id/approvals และ GET /api/v1/me/approvals */
export const ApprovalItem = z.object({
  id: z.uuid(),
  request_kind: ApprovalRequestKind,
  status: ApprovalStatus,
  needed: z.number().int(),
  received: z.number().int(),
  requested_by: z.object({ id: z.uuid(), display_name: z.string().nullable() }),
  note: z.string().nullable(),
  target_status_code: z.string().nullable(),
  ticket_id: z.string(),
  approvers: z.array(PendingApprover),
  decisions: z.array(ApprovalDecisionView),
  expires_at: z.iso.datetime().nullable(),
  resolved_at: z.iso.datetime().nullable(),
  created_at: z.iso.datetime(),
});
export type ApprovalItem = z.infer<typeof ApprovalItem>;

export const MyApprovalsQuery = z.object({ status: z.literal("pending").default("pending") });
export type MyApprovalsQuery = z.infer<typeof MyApprovalsQuery>;

/** POST /approve/:token — token เป็น credential (R4) · GET หน้ายืนยัน ไม่เขียนอะไร · POST นี่ต่างหากถึงตัดสิน */
export const ApproveTokenAction = z.object({
  decision: z.enum(["approve", "decline"]),
  note: z.string().max(5000).optional(),
});
export type ApproveTokenAction = z.infer<typeof ApproveTokenAction>;

/** ผลของการตัดสิน (decisions route / approve token POST) */
export const ApprovalDecisionResult = z.object({
  approval_id: z.uuid(),
  status: ApprovalStatus,
  received: z.number().int(),
  needed: z.number().int(),
  executed: z.enum(["applied", "skipped", "none"]).optional(),
  skip_reason: z.string().nullable().optional(),
});
export type ApprovalDecisionResult = z.infer<typeof ApprovalDecisionResult>;
